import React from 'react';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {ITsImageEditorController} from './ts-image-editor.controller';
import {TsSharingMode} from '../ts-sharing-mode/ts-sharing-mode.component';
import {requireImage} from '../../utils/utils';
import './ts-image-editor.style.scss';

export interface TsImageEditorProps {
    controller: ITsImageEditorController;
}

@observer
export class TsImageEditorComponent extends TechseeBaseComponent<TsImageEditorProps> {
    private containerRef = React.createRef<HTMLDivElement>();
    private canvasRef = React.createRef<HTMLCanvasElement>();

    private get controller() {
        return this.props.controller;
    }

    componentDidMount(): void {
        if (this.containerRef.current) {
            this.controller.setContainer(this.containerRef.current);
        }

        if (this.canvasRef.current) {
            this.controller.setCanvas(this.canvasRef.current);
        }
    }

    private handleTagsClick = (e: React.MouseEvent): void => {
        if (e.target === e.currentTarget) {
            this.controller.closeTags();
        }
    };

    private handleTagsPanelClick = (e: React.MouseEvent): void => {
        e.stopPropagation();
    };

    private handleTagButtonClick = (e: React.MouseEvent): void => {
        e.stopPropagation();
        this.controller.openTagsPanel(e.nativeEvent);
    };

    private handleTagsToggle = (): void => {
        this.controller.toggleTags();
    };

    private handleTagsChange = (newTags: string[]): void => {
        this.controller.updateTags(newTags);
    };

    private renderTagsInput(): JSX.Element {
        const {tags, maxTagLength} = this.controller;

        return (
            <ul className='tags'>
                {tags.map((tag, index) => (
                    <li key={index} className='tag-item'>
                        {tag}
                        <span
                            className='remove-button'
                            onClick={() => {
                                const newTags = tags.filter((_, i) => i !== index);
                                this.handleTagsChange(newTags);
                            }}>
                            ×
                        </span>
                    </li>
                ))}
                <li className='tag-item'>
                    <input
                        type='text'
                        placeholder='Add tag...'
                        maxLength={maxTagLength}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                                const newTag = e.currentTarget.value.trim();
                                if (!tags.includes(newTag)) {
                                    const newTags = [...tags, newTag];
                                    this.handleTagsChange(newTags);
                                    e.currentTarget.value = '';
                                }
                            }
                        }}
                    />
                </li>
            </ul>
        );
    }

    renderInternal(): JSX.Element {
        const {controller} = this.props;
        const tagsCount = controller.tags.length;

        const {
            tagsOpen,
            currentResource,
            shouldShowImageEditor,
            isSelectionMode,
            enableAnnotation,
            labelMode,
            isScanCaptureDesign,
            isRoundedFloatingVideo,
            isStretchedVideoMode,
            enableTagging,
            showAnnotationBar,
            showSharingMode,
            sharingModeActions,
            sharingModeData,
            isShareResourceReady,
            onShareClick,
            onShareActionClick,
            isCanvasReadyWithImage
        } = controller;

        return (
            <div className='ts-image-editor-outer-container' ref={this.containerRef}>
                <div
                    className='ts-image-editor-flex-container'
                    style={{display: shouldShowImageEditor ? 'flex' : 'none'}}>
                    <div className='ts-image-editor-inner-container' onClick={this.handleTagsClick}>
                        {/* Scanning label */}
                        {labelMode && !isScanCaptureDesign && !(isRoundedFloatingVideo && isStretchedVideoMode) && (
                            <div className='alert alert-info alert-floating image ts-fade-out'>
                                <h4>SCANNING</h4>
                            </div>
                        )}

                        {/* Canvas - hide visually until image is ready to avoid black flash, but keep layout space */}
                        <div
                            className={`canvas-container ${isCanvasReadyWithImage ? 'canvas-ready' : 'canvas-not-ready'}`}
                            style={{
                                visibility: isCanvasReadyWithImage ? 'visible' : 'hidden',
                                opacity: isCanvasReadyWithImage ? 1 : 0
                            }}>
                            <canvas ref={this.canvasRef}></canvas>
                        </div>

                        {/* Tag Button */}
                        {!tagsOpen && currentResource && enableTagging && isCanvasReadyWithImage && (
                            <button
                                type='button'
                                className='ts-image-editor-tag-button'
                                onClick={this.handleTagButtonClick}>
                                <img
                                    className='ts-image-editor-tag-image'
                                    src={requireImage('tag-white.png')}
                                    alt='tag'
                                />
                                {controller.tags.length > 0 && (
                                    <span className='tag-color'>({controller.tags.length})</span>
                                )}
                            </button>
                        )}

                        {/* Tags Panel */}
                        {tagsOpen && enableTagging && isCanvasReadyWithImage && (
                            <div className='tags-view-container' onClick={this.handleTagsPanelClick}>
                                <div
                                    className={`tags-view-top-bar ${tagsOpen ? 'open' : ''}`}
                                    onClick={this.handleTagsToggle}></div>
                                <div className='tags-view-content' style={{display: tagsOpen ? 'block' : 'none'}}>
                                    {this.renderTagsInput()}
                                </div>
                            </div>
                        )}

                        {/* Sharing modes */}
                        {showSharingMode &&
                            sharingModeActions &&
                            onShareClick &&
                            onShareActionClick &&
                            isCanvasReadyWithImage && (
                                <div className='sharing-modes'>
                                    <TsSharingMode
                                        actions={sharingModeActions}
                                        displayMode='left-to-right-on-left'
                                        data={sharingModeData}
                                        isResourceReady={isShareResourceReady}
                                        onShareClick={onShareClick}
                                        onShareActionClick={onShareActionClick}
                                    />
                                </div>
                            )}
                    </div>

                    {/*/!* Annotation Bar *!/*/}
                    {/*{enableAnnotation && showAnnotationBar && !isSelectionMode && (*/}
                    {/*    <div className='ts-annotation-bar-container'>*/}
                    {/*        <TsAnnotationBar*/}
                    {/*            dashboardTags={controller.dashboardTags}*/}
                    {/*            useNewInterface={true}*/}
                    {/*            annotationBarController={controller.annotationBarController}*/}
                    {/*        />*/}
                    {/*    </div>*/}
                    {/*)}*/}
                </div>
            </div>
        );
    }
}

export const TsImageEditor = TsImageEditorComponent;
export default TsImageEditor;
